import styles from "./TestbuyArticleDetailSheet.module.css";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import useTranslation from "../../../../../packages/locales/translations/util/useTranslation";
import { ItemCardWithLocation } from "@coworker/apprestructured/src/shared/components/item-cards/ItemCardWithLocation/ItemCardWithLocation";
import SimpleArticleCardSkeleton from "@coworker/apprestructured/src/shared/simple/SimpleSkeleton/SimpleArticleCardSkeleton";
import { NotFound } from "@coworker/apprestructured/src/task-management/components/NotFound/NotFound";
import FixaRadioButton from "@coworker/apprestructured/src/shared/wrappers/FixaRadioButton/FixaRadioButton";
import {
  useGetTestbuy,
  useUpdateTestbuyArticleCriterion,
} from "../../hooks/useTestbuyV2";
import type { Testbuy } from "../../services/testbuy.service";
import { useItemInfo } from "@coworker/apprestructured/src/shared/hooks/item/useItemInfo";
import type { Item } from "@coworker/apprestructured/src/shared/types/item";
import type {
  CriteriaState,
  Criterion as CriterionType,
  TestBuyProductWithCriteria,
} from "@coworker/types/lib/testbuy";
import { sortCriteriaDisplayOrder } from "@coworker/app/src/components/TestBuy/ArticleDetailPage/CriterionComponent/CriteriaHelper";
import useCriterionSVG from "@coworker/app/src/components/TestBuy/ArticleDetailPage/CriterionComponent/useCriterionSVG";
import useFormatter from "@coworker/app/src/hooks/useFormatter";
import { allCriteriaAnswered } from "@coworker/app/src/components/TestBuy/CommonComponents/article.helpers";
import { isDateWithinLastWeek } from "@coworker/app/src/components/TestBuy/CommonComponents/helpers";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import StyledModalFooter from "@coworker/apprestructured/src/task-management/components/StyledModalFooter/StyledModalFooter";
import { useCallback, useMemo } from "react";
import {
  CriterionKey,
  FailedCriteriaItem,
} from "@coworker/apprestructured/src/task-management/types/TestBuy";
import useSearchParamState, {
  encodeReturnTo,
} from "@coworker/apprestructured/src/shared/hooks/useSearchParamState";
import { useTask } from "@coworker/apprestructured/src/task-management/services/useTask";
import { useAllTaskTypes } from "@coworker/apprestructured/src/task-management/services/useTaskTypes";
import { TaskTypeNames } from "@coworker/apprestructured/src/task-management/constants/TaskTypeNames";
import { ItemNoAndTypeWithPriceAndCurrency } from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";

interface TestbuyArticleDetailSheetProps {
  visible: boolean;
  onClose: () => void;
  testbuyId: string;
  articleId: string;
}

interface CriterionRowProps {
  criterion: CriterionType;
  location: string;
  price: number;
  currency: string;
  disabled: boolean;
  onChange: (state: CriteriaState) => void;
}

const CRITERION_CONFIG: {
  [key in CriterionKey]?: {
    description?: boolean;
    location?: boolean;
    price?: boolean;
  };
} = {
  IsItCleanAndPerfect: {
    description: true,
  },
  IsItAvailableInIndicatedPosition: {
    location: true,
  },
  DoesItHaveTag: {
    price: true,
  },
};

// Criteria that can no longer be evaluated once the article isn't on display, and are
// therefore automatically marked as not okay alongside "IsItDisplayed" (mirrors the V1
// behaviour in updateCriterionReducer.ts).
const CASCADING_NOT_OK_CRITERIA: CriterionKey[] = [
  "IsItDisplayed",
  "IsItCleanAndPerfect",
  "AreBuyInstructionClear",
  "DoesItHaveTag",
];

const CriterionRow = ({
  criterion,
  location,
  price,
  currency,
  disabled,
  onChange,
}: CriterionRowProps) => {
  const { t } = useTranslation();
  const { formatCurrency } = useFormatter();
  const icon = useCriterionSVG(criterion.key);

  return (
    <div
      className={styles["criterion"]}
      data-testid={`criterion-${criterion.key}`}
    >
      <div className={styles["criterion-question"]}>
        <div className={styles["criterion-icon"]}>{icon}</div>

        <div className={styles["criterion-text"]}>
          <strong>{t("tb" + criterion.key)}</strong>

          {CRITERION_CONFIG[criterion.key]?.description && (
            <div className={styles["criterion-description"]}>
              {t("tb" + criterion.key + "Description")}
            </div>
          )}

          {CRITERION_CONFIG[criterion.key]?.location && (
            <div className={styles["criterion-description"]}>
              {t("pickupLocation")}: {location}
            </div>
          )}

          {CRITERION_CONFIG[criterion.key]?.price && (
            <div className={styles["criterion-description"]}>
              {t("priceQT")}: {formatCurrency(price, currency)}
            </div>
          )}
        </div>
      </div>

      <div className={styles["criterion-options"]}>
        <div
          className={styles["criterion-option-row"]}
          onClick={() => !disabled && onChange("OK")}
        >
          <FixaRadioButton
            id={`criterion-${criterion.key}-ok`}
            name={`criterion-${criterion.key}`}
            checked={criterion.state === "OK"}
            // The wrapping row's onClick above is the single source of truth for
            // toggling (it covers the whole row, not just the input/label), so this is
            // a no-op to avoid triggering the change twice.
            onChange={() => {}}
            label={t("tbOkay")}
            disabled={disabled}
            className={`${styles["criterion-option"]} ${styles["criterion-option--ok"]}`}
            data-testid={`criterionStateOkay-${criterion.key}`}
          />
        </div>

        <div
          className={styles["criterion-option-row"]}
          onClick={() => !disabled && onChange("NOT_OK")}
        >
          <FixaRadioButton
            id={`criterion-${criterion.key}-not-ok`}
            name={`criterion-${criterion.key}`}
            checked={criterion.state === "NOT_OK"}
            onChange={() => {}}
            label={t("tbNotOkay")}
            disabled={disabled}
            className={`${styles["criterion-option"]} ${styles["criterion-option--not-ok"]}`}
            data-testid={`criterionStateNotOkay-${criterion.key}`}
          />
        </div>

        <div
          className={styles["criterion-option-row"]}
          onClick={() => !disabled && onChange("NOT_OK_FIXED")}
        >
          <FixaRadioButton
            id={`criterion-${criterion.key}-not-ok-fixed`}
            name={`criterion-${criterion.key}`}
            checked={criterion.state === "NOT_OK_FIXED"}
            onChange={() => {}}
            label={t("tbNotOkayFixed")}
            disabled={disabled}
            className={`${styles["criterion-option"]} ${styles["criterion-option--not-ok-fixed"]}`}
            data-testid={`criterionStateNotOkayFixed-${criterion.key}`}
          />
        </div>
      </div>
    </div>
  );
};

interface CriteriaListProps {
  testbuyId: string;
  articleId: string;
  criteria: CriterionType[];
  location: string;
  price: number;
  currency: string;
  disabled: boolean;
}

const CriteriaList = ({
  testbuyId,
  articleId,
  criteria,
  location,
  price,
  currency,
  disabled,
}: CriteriaListProps) => {
  const sortedCriteria = useMemo(
    () => sortCriteriaDisplayOrder(criteria),
    [criteria]
  );
  const { mutate } = useUpdateTestbuyArticleCriterion(testbuyId);
  const isDisabled = disabled;

  const updateCriterion = useCallback(
    (criterion: CriterionType, state: CriteriaState) => {
      // Marking the article as not on display means none of the criteria that require
      // inspecting the article on the shelf can be answered either, so cascade the same
      // "not okay" state to them (mirrors the V1 behaviour).
      if (
        state === "NOT_OK" &&
        criterion.key === "IsItDisplayed" &&
        criterion.state !== "NOT_OK"
      ) {
        CASCADING_NOT_OK_CRITERIA.forEach((key) => {
          const cascadingCriterion = criteria.find((c) => c.key === key);
          if (cascadingCriterion) {
            mutate({
              articleId,
              criterion: { ...cascadingCriterion, state: "NOT_OK" },
            });
          }
        });
      } else {
        mutate({ articleId, criterion: { ...criterion, state } });
      }
    },
    [articleId, criteria, mutate]
  );

  return (
    <ol className={styles["criteria-list"]} data-testid="criterionList">
      {sortedCriteria.map((criterion) => (
        <CriterionRow
          key={criterion.key}
          criterion={criterion}
          location={location}
          price={price}
          currency={currency}
          disabled={isDisabled}
          onChange={(state) => updateCriterion(criterion, state)}
        />
      ))}
    </ol>
  );
};

interface TestbuyArticleDetailContentProps {
  testbuy: Testbuy;
  article: TestBuyProductWithCriteria;
  itemInfo: Item | null | undefined;
  isItemLoading: boolean;
}

const TestbuyArticleDetailContent = ({
  testbuy,
  article,
  itemInfo,
  isItemLoading,
}: TestbuyArticleDetailContentProps) => {
  // Once the testbuy session is no longer in progress, or a follow-up task has already been
  // created for this article, the checklist becomes read-only (mirrors the V1 behaviour).
  const isLocked = testbuy.status !== "IN_PROGRESS" || !!article.followUpTaskId;

  return (
    <div className={styles["sheet-content"]}>
      <div className={styles["item-card-wrapper"]}>
        {!itemInfo ? (
          <SimpleArticleCardSkeleton />
        ) : (
          <ItemCardWithLocation location={article.location} item={itemInfo} />
        )}
      </div>

      <CriteriaList
        testbuyId={testbuy.id}
        articleId={article.id!}
        criteria={article.criteria}
        location={article.location}
        price={article.price}
        currency={article.currency}
        disabled={isItemLoading || isLocked}
      />
    </div>
  );
};

export const TestbuyArticleDetailSheet = ({
  visible,
  onClose,
  testbuyId,
  articleId,
}: TestbuyArticleDetailSheetProps) => {
  const { t } = useTranslation();
  const [, setSearchParams] = useSearchParamState();
  const { data: testbuy, isLoading: isTestbuyLoading } =
    useGetTestbuy(testbuyId);
  const article = testbuy?.articles.find((a) => a.id === articleId);
  const { data: itemInfo, isLoading: isItemLoading } = useItemInfo(
    article?.shortId as string
  );
  const notFound = !isTestbuyLoading && (!testbuy || !article);

  const { data: taskTypes } = useAllTaskTypes();
  const testbuyFollowUpTaskTypeId = useMemo(
    () =>
      taskTypes?.find(({ name }) => name === TaskTypeNames.TESTBUY_FOLLOW_UP)
        ?.id,
    [taskTypes]
  );

  const isTaskCreated = !!article?.followUpTaskId;
  const { data: followUpTask } = useTask(article?.followUpTaskId ?? null);
  const isTaskResolved =
    followUpTask?.state === "completed" || followUpTask?.state === "closed";
  const taskFinishedAt = followUpTask?.completed_at
    ? new Date(followUpTask.completed_at).getTime()
    : followUpTask?.closed_at
      ? new Date(followUpTask.closed_at).getTime()
      : undefined;
  const isTaskViewable =
    isTaskCreated &&
    !!followUpTask &&
    (!isTaskResolved || isDateWithinLastWeek(taskFinishedAt));

  const handleComplete = () => {
    if (!testbuy || !article) return;

    if (article.criteria.every((c) => c.state !== "NOT_OK")) {
      // All criteria are okay (or were fixed on the spot): go back to the testbuy overview.
      setSearchParams((params) => {
        params.delete("testbuyArticleId");
        params.set("testbuyId", testbuy.id);
      });
    } else if (isTaskViewable) {
      // testbuyId/testbuyArticleId and taskViewer are never both set at the same time:
      // replace this sheet with the follow-up task's viewer, encoding a "returnTo" so
      // closing it lands back on the testbuy detail sheet (omitting this article's
      // detail view - see useSearchParamState).
      setSearchParams((params) => {
        params.set("returnTo", encodeReturnTo({ testbuyId: testbuy.id }));
        params.delete("testbuyId");
        params.delete("testbuyArticleId");
        params.set("taskViewer", article.followUpTaskId!);
      });
    } else if (testbuyFollowUpTaskTypeId) {
      const failedCriteriaItems: FailedCriteriaItem[] = article.criteria
        .filter((c) => c.state === "NOT_OK")
        .map((c) => ({ type: c.key, completed: false, comments: [] }));

      const item: ItemNoAndTypeWithPriceAndCurrency = {
        no: article.shortId,
        type: "ART",
        price: article.price,
        currency: article.currency,
      };

      const initialValues = {
        details: {
          testbuy_id: testbuy.id,
          article_id: article.id,
          item,
          location: {
            slid: article.location?.length === 6 ? article.location : "",
            custom:
              article.location?.length === 6 ? "" : article.location || "",
          },
          failed_criteria: failedCriteriaItems,
        },
      };

      // testbuyId/testbuyArticleId and taskCreator are never both set at the same time:
      // replace this sheet with the follow-up task creator, encoding a "returnTo" so
      // closing (or cancelling) it lands back on the testbuy detail sheet (omitting this
      // article's detail view).
      setSearchParams((params) => {
        params.set("returnTo", encodeReturnTo({ testbuyId: testbuy.id }));
        params.delete("testbuyId");
        params.delete("testbuyArticleId");
        params.set("taskCreator", testbuyFollowUpTaskTypeId);
        params.set("initialValues", JSON.stringify(initialValues));
      });
    }
  };

  return (
    <FixaModal
      visible={visible}
      handleCloseBtn={onClose}
      className={styles["modal"]}
    >
      <FixaSheets
        labelledById="testbuy-article-sheet-header"
        header={
          <FixaModalHeader
            id="testbuy-article-sheet-header"
            closeBtnClick={onClose}
          />
        }
        footer={
          article ? (
            <StyledModalFooter>
              <FixaButton
                text={t(isTaskCreated ? "showTaskString" : "completeButton")}
                type="primary"
                fluid
                disabled={
                  !allCriteriaAnswered(article.criteria) ||
                  (isTaskCreated && !isTaskViewable)
                }
                onClick={handleComplete}
                data-testid="testbuy-article-complete"
                size="medium"
              />
            </StyledModalFooter>
          ) : null
        }
        fullSize
      >
        {notFound ? (
          <NotFound title={t("noDataString")} message={t("noResultsString")} />
        ) : testbuy && article ? (
          <TestbuyArticleDetailContent
            testbuy={testbuy}
            article={article}
            itemInfo={itemInfo}
            isItemLoading={isItemLoading}
          />
        ) : (
          <div className={styles["sheet-content"]}>
            <SimpleArticleCardSkeleton />
          </div>
        )}
      </FixaSheets>
    </FixaModal>
  );
};
